import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Input, Alert, Logo } from '@heroui/react';
import Field from '../../components/common/Field';
import { authApi } from '../../api/authApi';
import { useUiStore } from '../../stores/uiStore';

/**
 * Phase 27a — public reset-password page. Reads the raw token from
 * the URL query string (`?token=…`), accepts a new password +
 * confirmation, and posts to /auth/reset-password. On success,
 * redirects to /login with a success-toast banner so the user can
 * sign in immediately.
 *
 * The same password strength rules as registration apply — 12+ chars,
 * uppercase + lowercase + digit + symbol. Server enforces these
 * independently; client-side validation is a UX courtesy.
 */

const resetSchema = z
  .object({
    password: z
      .string()
      .min(12, 'At least 12 characters')
      .regex(/[A-Z]/, 'Needs an uppercase letter')
      .regex(/[a-z]/, 'Needs a lowercase letter')
      .regex(/\d/, 'Needs a number')
      .regex(/[@$!%*?&#^]/, 'Needs a symbol'),
    confirmPassword: z.string(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match',
  });

type ResetFormData = z.infer<typeof resetSchema>;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const theme = useUiStore((s) => s.resolvedTheme);
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetFormData>({ resolver: zodResolver(resetSchema) });

  // Auto-redirect to /login after a brief success state.
  useEffect(() => {
    if (!success) return;
    const handle = window.setTimeout(() => navigate('/login'), 2500);
    return () => window.clearTimeout(handle);
  }, [success, navigate]);

  const onSubmit = async (data: ResetFormData) => {
    setError(null);
    try {
      await authApi.resetPassword(token, data.password);
      setSuccess(true);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      setError(message || 'This reset link is invalid or has expired.');
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-default-50">
        <div className="w-full max-w-sm space-y-6 p-6">
          <div className="flex flex-col items-center space-y-3">
            <Logo variant="stacked" size={140} onDark={theme === 'dark'} />
          </div>
          <Alert status="danger">
            This reset link is missing its token. Request a new link from the sign-in page.
          </Alert>
          <p className="text-center text-sm text-default-500">
            <Link to="/forgot-password" className="text-primary font-medium">
              Request a new link
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-default-50">
      <div className="w-full max-w-sm space-y-6 p-6">
        <div className="flex flex-col items-center space-y-3">
          <Logo variant="stacked" size={140} onDark={theme === 'dark'} />
          <h1 className="text-default-500 text-sm font-normal">Choose a new password</h1>
        </div>

        {success ? (
          <Alert status="success">
            Password updated. Redirecting you to sign in…
          </Alert>
        ) : (
          <>
            {error && <Alert status="danger">{error}</Alert>}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <Field label="New password" isRequired error={errors.password?.message}>
                <Input
                  type="password"
                  aria-label="New password"
                  placeholder="At least 12 characters"
                  autoComplete="new-password"
                  autoFocus
                  fullWidth
                  {...register('password')}
                />
              </Field>

              <Field
                label="Confirm new password"
                isRequired
                error={errors.confirmPassword?.message}
              >
                <Input
                  type="password"
                  aria-label="Confirm new password"
                  placeholder="Re-enter the password"
                  autoComplete="new-password"
                  fullWidth
                  {...register('confirmPassword')}
                />
              </Field>

              <Button
                type="submit"
                style={{
                  width: '100%',
                  backgroundColor: '#0D1B2A',
                  color: '#F0D060',
                  borderColor: '#0D1B2A',
                  opacity: isSubmitting ? 0.7 : 1,
                }}
                isDisabled={isSubmitting}
              >
                {isSubmitting ? 'Updating...' : 'Update password'}
              </Button>

              <p className="text-center text-sm text-default-500">
                <Link to="/login" className="text-primary font-medium">
                  Back to sign in
                </Link>
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
